import FullWidthBarBreadcrumbs from '@/components/Breadcrumb/FullWidthBarBreadcrumbs';
import LinkButton from '@/components/Button/LinkButton';
import CenteredContentSection from '@/components/ContentSection/CenteredContentSection';
import DescirptionContainer from '@/components/DescriptionList/DescriptionContainer';
import DescriptionRow from '@/components/DescriptionList/DescriptionRow';
import { H3, H4 } from '@/components/Element/Heading';
import VerticalLeadFormSection from '@/components/LeadForm/VerticalLeadFormSection';
import MetaTags from '@/components/Head/MetaTags';
import SimpleCenteredHeaderSection from '@/components/HeaderSection/SimpleCenteredHeaderSection';
import ListableImage from '@/components/Listable/ListableImage';
import {
  getInverterBySlug,
  // getInverters,
  getManufacturerBySlug,
} from 'data/database';
import { Inverter, Manufacturer } from 'data/types';
import { YEAR } from 'lib/constants';
import { ProductJsonLd } from 'next-seo';
import Link from 'next/link';

export async function getStaticPaths() {
  // const inverters = await getInverters();
  return {
    paths: [],
    // inverters.map((inverter) => ({
    //   params: {
    //     manufacturer_slug: inverter.manufacturerSlug,
    //     inverter_slug: inverter.slug,
    //   },
    // })),
    fallback: 'blocking', // false or 'blocking'
  };
}

type StaticProps = {
  params: {
    manufacturer_slug: string;
    inverter_slug: string;
  };
};

export async function getStaticProps({
  params: { manufacturer_slug, inverter_slug },
}: StaticProps) {
  const manufacturer = await getManufacturerBySlug(manufacturer_slug);
  const inverter = await getInverterBySlug(inverter_slug);
  return { props: { manufacturer, inverter } };
}

type Props = {
  manufacturer: Manufacturer;
  inverter: Inverter;
};

export default function Page({ manufacturer, inverter }: Props) {
  return (
    <article>
      <MetaTags
        title={`${manufacturer.name} ${inverter.name} Solar Inverter Manufacturer Guide`}
        description={`Learn about the ${manufacturer.name} ${inverter.name} solar inverter. Explore solar inverter efficiency, voltage, warranties and more.`}
      />
      <ProductJsonLd
        productName={inverter.name}
        brand={manufacturer.name}
        manufacturerName={manufacturer.name}
        manufacturerLogo={manufacturer.webpPath}
        images={inverter.webpPath ? [inverter.webpPath] : []}
        offers={[
          {
            lowPrice: 6000,
            price: 12000,
            highPrice: 100000,
            url: `https://energypal.com/best-solar-inverters-for-homes/${manufacturer.slug}/${inverter.slug}`,
            priceCurrency: 'USD',
            priceValidUntil: '2026-01-01',
            itemCondition: 'https://schema.org/NewCondition',
            availability: 'https://schema.org/InStock',
            seller: {
              '@type': 'Organization',
              name: 'EnergyPal',
              url: 'https://energypal.com',
            },
          },
        ]}
      />
      <ProductJsonLd
        productName={inverter.name}
        brand={manufacturer.name}
        manufacturerName={manufacturer.name}
        manufacturerLogo={manufacturer.webpPath}
        images={
          inverter.webpPath
            ? [inverter.webpPath]
            : [
                'https://energypal.com/_next/image?url=https%3A%2F%2Fcdn.energypal.com%2Finverters%2Fiq7plus-72-x-us-240%2Fiq7plus-72-x-us-240-product.webp&w=256&q=75',
              ]
        }
        offers={[
          {
            lowPrice: 6000,
            price: 12000,
            highPrice: 100000,
            url: `https://energypal.com/best-solar-inverters-for-homes/${manufacturer.slug}/${inverter.slug}`,
            priceCurrency: 'USD',
            priceValidUntil: '2026-01-01',
            itemCondition: 'https://schema.org/NewCondition',
            availability: 'https://schema.org/InStock',
            seller: {
              '@type': 'Organization',
              name: 'EnergyPal',
              url: 'https://energypal.com',
            },
          },
        ]}
      />

      <SimpleCenteredHeaderSection
        dark
        supTitle={`Buyer's Guide ${YEAR}`}
        title={`${manufacturer.name} ${inverter.name}`}
        subTitle="Solar Inverter Spec Datasheet"
      />
      <FullWidthBarBreadcrumbs
        pages={[
          {
            name: 'Resources',
            href: '/solar-customer-resources',
          },
          {
            name: 'Best Solar Inverters for Homes',
            href: '/best-solar-inverters-for-homes',
          },
          {
            name: manufacturer.name,
            href: `/best-solar-inverters-for-homes/${manufacturer.slug}`,
          },
          {
            name: inverter.name,
            href: `/best-solar-inverters-for-homes/${manufacturer.slug}/${inverter.slug}`,
          },
        ]}
      />
      <CenteredContentSection>
        <ListableImage listable={inverter} className="text-center" />
        <DescirptionContainer>
          <DescriptionRow label="Model">{inverter.name}</DescriptionRow>
          <DescriptionRow label="Manufacturer">
            <Link href={`/best-home-battery-systems/${manufacturer.slug}`}>
              {manufacturer.name}
            </Link>
          </DescriptionRow>
          {inverter.features?.map((characteristic) => (
            <DescriptionRow key={characteristic.key} label={characteristic.key}>
              {characteristic.value}
            </DescriptionRow>
          ))}
        </DescirptionContainer>
        {inverter.datasheetPath && (
          <>
            <div className="text-center">
              <H3>
                {manufacturer.name} Solar Inverter Spec Datasheet{' '}
                {inverter.name}
              </H3>
              <LinkButton href={inverter.datasheetPath}>
                Download Datasheet
              </LinkButton>
            </div>
          </>
        )}

        <H4>Inverter Info</H4>
        <DescirptionContainer>
          {inverter.overview?.map((characteristic) => (
            <DescriptionRow key={characteristic.key} label={characteristic.key}>
              {characteristic.value}
            </DescriptionRow>
          ))}
        </DescirptionContainer>

        {inverter.features && (
          <>
            <H4>Inverter Features</H4>
            <DescirptionContainer>
              {inverter.features.map((characteristic) => (
                <DescriptionRow
                  key={characteristic.key}
                  label={characteristic.key}
                >
                  {characteristic.value}
                </DescriptionRow>
              ))}
            </DescirptionContainer>
          </>
        )}

        {inverter.voltage && (
          <>
            <H4>Inverter Voltage</H4>
            <DescirptionContainer>
              {inverter.voltage.map((characteristic) => (
                <DescriptionRow
                  key={characteristic.key}
                  label={characteristic.key}
                >
                  {characteristic.value}
                </DescriptionRow>
              ))}
            </DescirptionContainer>
          </>
        )}

        <H4>
          Buy {manufacturer.name} {inverter.name} Solar Inverters
        </H4>
        <p>
          EnergyPal is a dealer for the best Solar inverters and leading energy
          companies, solar installers and manufacturers of solar and battery
          storage products. To see our latest deals, and learn about financing
          and $0 down lease options, please request a quote and we will call you
          immediately.
        </p>
      </CenteredContentSection>
      <VerticalLeadFormSection />
    </article>
  );
}
